import { useCallback, useMemo } from 'react';
import { useLocation, useHistory } from 'react-router-dom';

/**
 * Hook that gives you access to the query string (search params) in a
 * React‑Router v5 environment.
 *
 * It behaves similarly to `useSearchParams` from react‑router‑dom v6:
 *  * returns `[URLSearchParams, setSearchParams]`
 *  * `setSearchParams` accepts either an object or a function that receives
 *    the current `URLSearchParams` and returns an object.
 *
 * Note: This hook always uses `history.replace` to change the URL without
 * creating a new entry in the history stack.  If you need a push, call
 * `history.push` manually.
 */
export default function useSearchParams(): [
  URLSearchParams,
  (
    newParams:
      | Record<string, any>
      | ((prev: URLSearchParams) => Record<string, any>)
  ) => void
] {
  const { pathname, search } = useLocation();
  const history = useHistory();

  // Memoize the URLSearchParams instance so we only re‑create it when `search` changes.
  const params = useMemo(() => new URLSearchParams(search), [search]);

  /**
   * Update the query string.
   *
   * @param newParamsOrFn Either:
   *   • an object like `{ foo: '1', bar: null }` (null/undefined keys are removed)
   *   • a function `prevParams => ({ ... })` that receives the current `URLSearchParams`
   */
  const setSearchParams = useCallback(
    (
      newParamsOrFn:
        | Record<string, any>
        | ((prev: URLSearchParams) => Record<string, any>)
    ) => {
      const nextParams = new URLSearchParams(search);

      // Resolve the incoming argument – either a function or an object.
      let updates: Record<string, any>;
      if (typeof newParamsOrFn === 'function') {
        // `newParamsOrFn` should return a plain object.
        updates = newParamsOrFn(params);
      } else {
        updates = newParamsOrFn;
      }

      // Mutate `nextParams` based on the updates.
      Object.entries(updates).forEach(([key, value]) => {
        if (value === null || value === undefined) {
          nextParams.delete(key);
        } else {
          nextParams.set(key, String(value));
        }
      });

      history.replace({
        pathname,
        search: nextParams.toString()
      });
    },
    [history, params, pathname, search]
  );

  return [params, setSearchParams] as const;
}

